import { reactive } from 'vue'
import JSZip from 'jszip'
import { message } from 'ant-design-vue'
import request from '/@/api/http/request'
import { CURRENT_CONFIG } from '/@/api/http/config'

// Export ZIP d'un workspace (annotations KML + médias) exécuté en arrière-plan :
// l'état vit au niveau module, donc l'export survit à la navigation et au démontage
// de la page Workspaces. Un widget global (ZipExportProgress) affiche la progression
// et le téléchargement se déclenche tout seul à la fin.

export interface ZipExportJob {
  id: string // workspace_id
  name: string // workspace_name
  status: 'running' | 'done' | 'error'
  stage: string
  current: number // médias téléchargés
  total: number // médias au total (0 = indéterminé)
}

// Un job par workspace. Réactif et partagé entre tous les appelants du hook.
const jobs = reactive<Record<string, ZipExportJob>>({})

// ── Helpers KML ────────────────────────────────────────────────────────────────
function hexToKmlColor (hex: string, alpha = 1): string {
  const c = hex.replace('#', '').padStart(6, '0')
  const a = Math.round(alpha * 255).toString(16).padStart(2, '0')
  return `${a}${c.slice(4, 6)}${c.slice(2, 4)}${c.slice(0, 2)}`
}

function circleToKmlCoords (center: number[], radiusM: number, steps = 64): string {
  const [lng, lat] = center
  const earthRadius = 6371000
  const coords: string[] = []
  for (let i = 0; i <= steps; i++) {
    const angle = (i * 2 * Math.PI) / steps
    const dlat = (radiusM * Math.cos(angle)) / earthRadius * (180 / Math.PI)
    const dlng = (radiusM * Math.sin(angle)) / (earthRadius * Math.cos(lat * Math.PI / 180)) * (180 / Math.PI)
    coords.push(`${lng + dlng},${lat + dlat},0`)
  }
  return coords.join(' ')
}

// Groupes contenant les tracés (vols + positions utilisateurs) : leurs éléments sont
// forcés en blanc. Les anciens noms sont conservés pour les données créées avant le renommage.
const FLIGHT_RECORD_GROUP_NAMES = ['Historique déplacement', 'Historique Vols', 'Historique Positions', 'Flight Record']

function groupsToKmlFolders (groups: any[]): string {
  const rootGroups = groups.filter((g: any) => !g.parent_id)
  function renderGroup (g: any, inFlightRecord = false): string {
    const isFlightRecord = inFlightRecord || FLIGHT_RECORD_GROUP_NAMES.includes(g.name)
    const elements: any[] = g.elements ?? []
    const subGroups = groups.filter((s: any) => s.parent_id === g.id)
    const placemarks = elements.map(el => {
      const geom = el.resource?.content?.geometry
      const props = el.resource?.content?.properties ?? {}
      // Tracés de vol : blanc par défaut.
      const color = isFlightRecord ? '#FFFFFF' : (props.color || '#2D8CF0')
      if (!geom) return ''
      let coordStr = ''
      if (geom.type === 'Circle') {
        const coords = circleToKmlCoords(geom.coordinates, geom.radius)
        coordStr = `<Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon>`
      } else if (geom.type === 'Point') {
        coordStr = `<Point><coordinates>${geom.coordinates.join(',')}</coordinates></Point>`
      } else if (geom.type === 'LineString') {
        coordStr = `<LineString><coordinates>${geom.coordinates.map((c: number[]) => c.join(',')).join('\n')}</coordinates></LineString>`
      } else if (geom.type === 'Polygon') {
        coordStr = `<Polygon><outerBoundaryIs><LinearRing><coordinates>${(geom.coordinates[0] ?? []).map((c: number[]) => c.join(',')).join('\n')}</coordinates></LinearRing></outerBoundaryIs></Polygon>`
      }
      if (!coordStr) return ''
      return `<Placemark>
  <name>${el.name}</name>
  <Style>
    <LineStyle><color>${hexToKmlColor(color)}</color><width>2</width></LineStyle>
    <PolyStyle><color>${hexToKmlColor(color, 0.4)}</color></PolyStyle>
    <IconStyle><color>${hexToKmlColor(color)}</color></IconStyle>
  </Style>
  ${coordStr}
</Placemark>`
    }).filter(Boolean)
    const subFolders = subGroups.map((s: any) => renderGroup(s, isFlightRecord))
    return `<Folder>
  <name>${g.name}</name>
  ${placemarks.join('\n')}
  ${subFolders.join('\n')}
</Folder>`
  }
  return rootGroups.map((g: any) => renderGroup(g)).join('\n')
}

function buildKml (groups: any[], docName: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${docName}</name>
    ${groupsToKmlFolders(groups)}
  </Document>
</kml>`
}

function triggerDownload (blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ── API publique ─────────────────────────────────────────────────────────────
export function useZipExport () {
  function isExporting (workspaceId: string): boolean {
    return jobs[workspaceId]?.status === 'running'
  }

  function dismiss (workspaceId: string) {
    delete jobs[workspaceId]
  }

  async function startExport (ws: { workspace_id: string; workspace_name: string }) {
    const id = ws.workspace_id
    if (jobs[id]?.status === 'running') {
      message.info(`Un export de « ${ws.workspace_name} » est déjà en cours`)
      return
    }
    const job: ZipExportJob = { id, name: ws.workspace_name, status: 'running', stage: 'Préparation…', current: 0, total: 0 }
    jobs[id] = job
    const base = CURRENT_CONFIG.baseURL

    try {
      const zip = new JSZip()

      // ── 1. Annotations (KML) ─────────────────────────────────────────────────
      job.stage = 'Annotations…'
      let allGroups: any[] = []
      try {
        // Pas de filtre is_distributed : on veut TOUTES les annotations, y compris
        // les tracés ("Historique déplacement", groupes non distribués à la RC).
        const r = await request.get(`/map/api/v1/workspaces/${id}/element-groups`, {
          params: { group_id: '' },
          baseURL: base,
        })
        const data = r.data?.data
        allGroups = Array.isArray(data) ? data : (data?.list ?? [])
      } catch (e) {
        message.warning('Impossible de récupérer les annotations')
      }
      zip.file(`${ws.workspace_name}.kml`, buildKml(allGroups, ws.workspace_name))

      // ── 2. Médias ────────────────────────────────────────────────────────────
      job.stage = 'Liste des médias…'
      let mediaFiles: any[] = []
      try {
        const r = await request.get(`/media/api/v1/files/${id}/files`, {
          params: { page: 1, page_size: 500 },
          baseURL: base,
        })
        mediaFiles = r.data?.data?.list ?? []
      } catch (e) {
        message.warning('Impossible de récupérer la liste des médias')
      }
      job.total = mediaFiles.length

      // Les coups de cœur sont rangés dans un dossier à part ; les autres médias
      // restent dans "media". Le sous-dossier "coups-de-coeur" n'est créé que s'il
      // y a au moins un favori.
      const mediaFolder = zip.folder('media')!
      let favFolder: JSZip | null = null
      for (const file of mediaFiles) {
        job.stage = `Médias… (${job.current + 1}/${job.total})`
        try {
          // Step 1: get pre-signed URL as JSON from /preview-url
          const urlRes = await request.get(
            `/media/api/v1/files/${id}/file/${file.file_id}/preview-url`,
            { baseURL: base }
          )
          const signedUrl: string = urlRes.data?.data
          if (signedUrl) {
            // Step 2: download actual file from signed URL (bypass axios interceptors with plain fetch)
            const fileRes = await fetch(signedUrl)
            if (fileRes.ok) {
              const blob = await fileRes.blob()
              const fileName = file.file_name || file.name || file.file_id || 'file'
              if (file.favorited) {
                if (!favFolder) favFolder = zip.folder('coups-de-coeur')!
                favFolder.file(fileName, blob)
              } else {
                mediaFolder.file(fileName, blob)
              }
            }
          }
        } catch (e) {
          // Skip individual file errors
        }
        job.current++
      }

      // ── 3. Compression + téléchargement ──────────────────────────────────────
      job.stage = 'Compression…'
      const blob = await zip.generateAsync({ type: 'blob' })
      triggerDownload(blob, `${ws.workspace_name}.zip`)
      job.status = 'done'
      job.stage = 'Terminé'
      message.success(`Export « ${ws.workspace_name} » téléchargé`)
      // Auto-nettoyage du job terminé au bout de quelques secondes.
      setTimeout(() => { if (jobs[id]?.status === 'done') delete jobs[id] }, 8000)
    } catch (e) {
      job.status = 'error'
      job.stage = 'Échec'
      message.error(`Erreur lors de l'export « ${ws.workspace_name} »`)
    }
  }

  return { jobs, startExport, isExporting, dismiss }
}
